import React from "react";

// Mui Styles
import { StyledPaper } from "@/styles/mui/globalStyles";
import { TableListTheme } from "@/styles/mui/tableListTheme";
import AddBoxIcon from "@mui/icons-material/AddBox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider, styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { GridEditInputCell } from "@mui/x-data-grid";

// CSS
import "@/styles/page/systemSettingProductPage.css";

// Navigate
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Components
import DataTable from "@/components/dataTable";
import Layout from "@/components/layout";
import Title from "@/components/title";

// Mui Icon
import DownloadIcon from "@mui/icons-material/Download";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import SaveIcon from "@mui/icons-material/Save";

import { exportCSVFile } from "@/config/csvConfig";
import CreateSystemSettingWorkDialog from "../components/Dialog/createSystemSettingWorkDialog";
import { editWorkLocation } from "../fetch/fetchWork";

// Fetch
import {
  getAllShopTargetList,
  updatedSalesMonthlyTarget,
} from "@/fetch/fetchTarget";

// Moment
import Moment from "moment";
import { extendMoment } from "moment-range";
import "moment/locale/zh-hk";
import validator from "validator";

import _ from "lodash";
const SystemSettingWorkPage = (props) => {
  const { open, setOpen } = props;
  const moment = extendMoment(Moment);
  const fileReader = new FileReader();

  const { date } = useParams();
  const datetime = moment(`${date}-01`).format("YYYY-MM-DD");
  const location = useLocation();

  const mode = location?.state?.mode;
  const disabled = mode === "view" || !mode;
  //ref
  const productDataRef = React.useRef([]);

  // Navigate
  const navigate = useNavigate();

  const targetMonthlyTableColumn = [
    {
      field: "work_shortcode",
      headerName: "店舖編號",
      minWidth: 120,
      flex: 1,
      editable: false,
    },
    {
      field: "work_name",
      headerName: "店舖名稱",
      minWidth: 150,
      flex: 1,
      editable: false,
    },
    {
      field: "work_district",
      headerName: "店舖地區",
      minWidth: 120,
      flex: 1,
      editable: false,
    },
    {
      field: "work_address",
      headerName: "店舖地址",
      width: 300,
      flex: 2,
      editable: false,
    },
    {
      field: "work_telephone",
      headerName: "店舖電話",
      minWidth: 120,
      flex: 1,
      editable: false,
    },
    {
      field: "target_datetime",
      headerName: "月份",
      minWidth: 120,
      flex: 1,
      editable: false,
    },
    {
      field: "street",
      headerName: "street",
      minWidth: 120,
      flex: 1,
      editable: !disabled,
    },
    {
      field: "target_total_amount",
      headerName: "目標總額",
      minWidth: 120,
      flex: 1,
      editable: !disabled,
      preProcessEditCellProps: (params) => {
        const hasError = () => {
          let errorMessage = "";
          if (validator.isEmpty(params.props.value.trim())) {
            errorMessage = "Empty value is not allowed";
          } else if (!validator.isNumeric(params.props.value)) {
            errorMessage = "Value may contain string/text/character";
          } else {
            errorMessage = null;
          }
          return errorMessage;
        };
        return { ...params.props, error: hasError() };
      },
      renderEditCell: systemSettingWorkRenderEditCell,
    },
    {
      field: "is_active",
      headerName: "狀態",
      editable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={params.row.is_active ? "開啓" : "關閉"}
            color={params.row.is_active ? "success" : "error"}
          />
        );
      },
    },
  ];
  //create a Edit component for grid data use when the data are edited
  function systemSettingWorkRenderEditCell(props) {
    const { error } = props;
    const StyledTooltip = styled(({ className, ...props }) => (
      <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
      [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      },
    }));

    return (
      <StyledTooltip open={!!error} title={error}>
        <GridEditInputCell {...props} />
      </StyledTooltip>
    );
  }
  // States
  const [shopData, setShopData] = React.useState([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [editCount, setEditCount] = React.useState(0);
  const [dataTableLoading, setDataTableLoading] = React.useState(false);

  const handleOnClickSave = async () => {
    await updatedSalesMonthlyTarget(productDataRef.current)
      .then((data) => {
        if (data && data.results) {
          productDataRef.current = [];
          setEditCount((prev) => (prev = 0));
          setSnackMessage(`修改成功`);
        } else {
          setSnackMessage(`修改失敗`);
        }
        setSnackOpen(true);
        setTimeout(() => {
          setSnackOpen(false);
        }, 1500);
      })
      .then(fetchAll);
  };
  const handleRowUpdate = (updatedRow) => {
    let rawData = {
      id: updatedRow?.id,
      target_datetime: updatedRow?.target_datetime,
      target_total_amount: updatedRow?.target_total_amount,
      is_active: updatedRow?.is_active,
      street: updatedRow?.street,
    };
    const updatedRows = [...productDataRef.current];

    const index = updatedRows.findIndex((item) => item.id === updatedRow.id);

    if (index >= 0) {
      updatedRows[index] = rawData;
    } else {
      updatedRows.push(rawData);
    }

    productDataRef.current = updatedRows;
    setEditCount((prev) => prev + 1);
  };
  const handleCellEditCommitChange = (data, event) => {
    if (data.row[`${data["field"]}`] !== data.value) {
      let rawData = {
        id: data.row?.id,
        target_datetime: data.row?.target_datetime,
        target_total_amount: data.row?.target_total_amount,
        is_active: data.row?.is_active,
      };

      rawData = { ...rawData, [`${data["field"]}`]: data.value };

      const updatedRows = [...productDataRef.current];

      const tempValue = updatedRows.filter((item) => item.id !== rawData.id);

      tempValue.push(rawData);

      productDataRef.current = tempValue;
      setEditCount((prev) => prev + 1);
    }
  };

  const handleCellClickChange = (data, newValue) => {
    let rawData = {
      id: data.row?.id,
      target_datetime: data.row?.target_datetime,
      target_total_amount: data.row?.target_total_amount,
      is_active: data.row?.is_active,
    };

    rawData = { ...rawData, [`${data["field"]}`]: newValue };

    const updatedRows = [...productDataRef.current];

    const tempValue = updatedRows.filter((item) => item.id !== rawData.id);

    tempValue.push(rawData);

    productDataRef.current = tempValue;

    setEditCount((prev) => prev + 1);
  };

  const handleCellClick = (data) => {
    if (data["field"] === "is_active") {
      const tempData = [...shopData];
      const index = shopData.findIndex((item) => item.id === data.id);
      if (index > -1) {
        tempData[index]["is_active"] = !shopData[index]["is_active"];
        handleCellClickChange(data, tempData[index]["is_active"]);
      }
      setShopData(tempData);
    }
  };
  const handleOnChange = (e) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (file) {
      fileReader.onload = async function (event) {
        const text = event.target.result;

        const quoteChar = '"';
        const delimiter = ",";

        const regex = new RegExp(
          `\\s*(${quoteChar})?(.*?)\\1\\s*(?:${delimiter}|$)`,
          "gs"
        );

        const match = (line) =>
          [...line.matchAll(regex)]
            .map((m) => m[2]) // we only want the second capture group
            .slice(0, -1); // cut off blank match at the end

        var lines = text.split("\n");
        var result = [];

        // Read first row and display first column
        // var readVersion = lines[0].split(",");
        // var version = readVersion[0];

        var headers = match(lines.shift());
        // var headers = lines[1].split(",");
        // for (let i = 0; i < headers.length; i++) {
        //   headers[i] = headers[i].replace(`\r`, "");
        // }

        // Rows, skip 0
        for (var i = 0; i < lines.length; i++) {
          if (lines[i].length > 0) {
            var obj = {};
            var currentline = match(lines[i]);

            // Columns
            for (var j = 0; j < headers.length; j++) {
              if (currentline[j] !== "" && currentline[j] !== undefined) {
                if (headers[j] === "target_total_amount") {
                  if (isNaN(parseInt(currentline[j]))) {
                    setSnackMessage(
                      "target_total_amount is not a valid number value"
                    );
                    setSnackOpen(true);
                    setTimeout(() => {
                      setSnackOpen(false);
                    }, 1500);
                    return false;
                  } else {
                    obj[headers[j]] = currentline[j];
                  }
                } else {
                  obj[headers[j]] = currentline[j];
                }
              }
            }
            result.push(obj);
          }
        }

        // Output
        await editWorkLocation(datetime, result).then((data) => {
          if (data.results) {
            setSnackMessage(`修改成功`);
          } else {
            setSnackMessage(`修改失敗`);
          }
          setSnackOpen(true);
          setTimeout(() => {
            setSnackOpen(false);
          }, 1500);
        });
        fetchAll();
      };

      fileReader.readAsText(file);
    }
    e.target.value = null;
  };
  // Fetch
  const fetchAll = async () => {
    try {
      setDataTableLoading(true);
      await getAllShopTargetList(datetime).then((data) => {
        if (data && data.results) {
          setShopData(data.data);
        } else {
          setShopData([]);
        }
      });
    } catch (error) {
    } finally {
      setDataTableLoading(false);
    }
  };

  // Browser Back
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? All provided data will be lost.";
    e.returnValue = message;
    return message;
  };
  const exportWorkLocation = async () => {
    const headers = {
      id: "id",
      work_shortcode: "work_shortcode",
      work_name: "work_name",
      work_district: "work_district",
      work_address: "work_address",
      work_telephone: "work_telephone",
      target_total_amount: "target_total_amount",
      street: "street",
      is_active: "is_active",
    };
    const { data: targetList } = await getAllShopTargetList(datetime);
    const csvData = _.map(targetList, (data) => {
      return {
        id: data.id,
        work_shortcode: `"${data.work_shortcode}"`,
        work_name: `"${data.work_name}"`,
        work_district: `"${data.work_district}"`,
        work_address: `"${data.work_address}"`,
        work_telephone: `"${data.work_telephone}"`,
        target_total_amount: `"${data.target_total_amount}"`,
        street: `${
          data.street !== null && data.street !== "null"
            ? `"${data.street}"`
            : ""
        }`,
        is_active: data.is_active,
      };
    });
    exportCSVFile(headers, csvData, `Work Location List - ${datetime}`);
  };
  React.useEffect(() => {
    fetchAll();

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout
      header="StaffManagePage"
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title={`${date}店鋪設定`}
        history={() => navigate(-1)}
        children={
          <div>
            {!disabled && (
              <React.Fragment>
                <IconButton
                  component="label"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <AddBoxIcon />
                </IconButton>
                {/* Save Record */}
                <IconButton
                  component="label"
                  disabled={editCount <= 0 ? true : false}
                  color={"primary"}
                  onClick={() => handleOnClickSave()}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton component="label">
                  <DriveFolderUploadIcon />
                  <input
                    hidden
                    type={"file"}
                    id={"csvFileInput"}
                    accept={".csv"}
                    onChange={(e) => handleOnChange(e)}
                  />
                </IconButton>
              </React.Fragment>
            )}
            <IconButton component="label" onClick={() => exportWorkLocation()}>
              <DownloadIcon />
            </IconButton>
          </div>
        }
      />
      <CreateSystemSettingWorkDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        datetime={datetime}
        setSnackSeverity={setSnackSeverity}
        setSnackOpen={setSnackOpen}
        setSnackMessage={setSnackMessage}
        fetchAll={fetchAll}
        productData={shopData}
      ></CreateSystemSettingWorkDialog>
      <StyledPaper page={"yes"}>
        <ThemeProvider theme={TableListTheme}>
          <DataTable
            loading={dataTableLoading}
            rows={shopData}
            columns={targetMonthlyTableColumn}
            pageSize={100}
            onCellClick={(param, event) => {
              if (disabled) return;
              handleCellClick(param);
            }}
            processRowUpdate={(updatedRow, originalRow) => {
              if (disabled || updatedRow === originalRow) return;
              handleRowUpdate(updatedRow);
            }}
            // onCellEditStop={(data, event) => {
            //   if (disabled) return;
            //   handleCellEditCommitChange(data, event);
            // }}
          />
        </ThemeProvider>
      </StyledPaper>
    </Layout>
  );
};

export default SystemSettingWorkPage;
